// src/components/PhoneAuthTest.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../firebase';
import { createUserDocIfNotExists } from '../AuthService';
import { COUNTRY_CODES } from '../utils/countryCodes';

function PhoneAuthTest() {
  // Separate dial code & local phone
  const [selectedDialCode, setSelectedDialCode] = useState('+1');
  const [localNumber, setLocalNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);

  const recaptchaContainerRef = useRef(null);

  // 1) Wrap setupRecaptcha in useCallback so ESLint sees it as a stable dependency
  const setupRecaptcha = useCallback(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        'recaptcha-container',
        {
          size: 'invisible',
          callback: () => {
            console.log('✅ reCAPTCHA verified');
          },
          'expired-callback': () => {
            console.warn('⚠️ reCAPTCHA expired, resetting...');
            // Clear and re-initialize recaptcha
            window.recaptchaVerifier.clear();
            delete window.recaptchaVerifier;
            setupRecaptcha();
          },
        },
        auth
      );
      window.recaptchaVerifier.render().then((widgetId) => {
        window.recaptchaWidgetId = widgetId;
      });
    }
  }, []); // no external dependencies

  // 2) Call setupRecaptcha in an effect, listing it in the dependency array
  useEffect(() => {
    setupRecaptcha();
  }, [setupRecaptcha]);

  // Format phone (dial code + local)
  const getFullPhoneNumber = () => {
    return (selectedDialCode + localNumber).trim();
  };

  // Send verification code
  async function handleSendCode() {
    const fullPhone = getFullPhoneNumber();

    if (!fullPhone || fullPhone.length < 5) {
      alert('Please enter a valid phone number.');
      return;
    }

    try {
      const appVerifier = window.recaptchaVerifier;
      const result = await signInWithPhoneNumber(auth, fullPhone, appVerifier);
      setConfirmationResult(result);
      alert('📲 SMS code sent!');
    } catch (err) {
      console.error('❌ Error sending SMS code:', err);
      alert(err.message);
    }
  }

  // Verify code after user receives SMS
  async function handleVerifyCode() {
    if (!confirmationResult || !verificationCode) {
      return alert('Please enter the SMS code!');
    }

    try {
      const cred = await confirmationResult.confirm(verificationCode);
      alert('✅ Phone auth success! UID: ' + cred.user.uid);

      await createUserDocIfNotExists(cred.user);

      setLocalNumber('');
      setVerificationCode('');
      setConfirmationResult(null);
    } catch (err) {
      console.error('❌ Error verifying code:', err);
      alert(err.message);
    }
  }

  return (
    <div style={{ padding: 20 }}>
      <h2>Phone Auth Test</h2>

      {/* Where reCAPTCHA will render (invisible) */}
      <div id="recaptcha-container" ref={recaptchaContainerRef}></div>

      {/* Dial code + local input */}
      <div style={{ marginBottom: '1em' }}>
        <label>Phone Number:</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={selectedDialCode}
            onChange={(e) => setSelectedDialCode(e.target.value)}
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.dial_code} value={c.dial_code}>
                {c.name} ({c.dial_code})
              </option>
            ))}
          </select>

          <input
            type="tel"
            value={localNumber}
            onChange={(e) => setLocalNumber(e.target.value)}
            placeholder="555-555-1234"
          />
        </div>

        <button onClick={handleSendCode}>Send Code</button>
      </div>

      {/* If SMS code was sent, show verification input */}
      {confirmationResult && (
        <div>
          <label>Verification Code:</label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="123456"
          />
          <button onClick={handleVerifyCode}>Verify</button>
        </div>
      )}
    </div>
  );
}

export default PhoneAuthTest;
